// src/components/financials/GivingAnalysisReport.js
// Updated: 01/25/2026 - Excludes Opening Balances from Donor Metrics

import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Grid, Table, TableBody, TableCell, 
  TableHead, TableRow, LinearProgress, Card, CardContent,
  TextField, MenuItem
} from '@mui/material';
import { People, TrendingUp, DonutLarge, CalendarToday } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const GivingAnalysisReport = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  
  const todayYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(todayYear);
  
  const yearOptions = [];
  for (let i = todayYear - 3; i <= todayYear + 2; i++) {
      yearOptions.push(i);
  }

  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalGivers: 0,
    averageGiving: 0,
    medianGiving: 0,
    top10Concentration: 0,
    bands: []
  });

  const churchId = userProfile?.churchId;

  useEffect(() => {
    if (!churchId) return;
    
    const analyzeGiving = async () => {
      setLoading(true);
      
      try {
        // 1. Fetch Household/Unit Mapping
        const unitsSnap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'givingUnits'));
        const memberToUnitMap = {}; 
        unitsSnap.docs.forEach(doc => {
          const data = doc.data();
          if (data.memberIds && Array.isArray(data.memberIds)) {
            data.memberIds.forEach(mId => {
              memberToUnitMap[mId] = doc.id; 
            });
          }
        });

        // 2. Fetch Transactions for the SELECTED year
        const q = query(
          collection(db, 'churches', churchId, 'financials', 'data', 'transactions'),
          where('status', '==', 'posted'),
          where('date', '>=', `${selectedYear}-01-01`),
          where('date', '<=', `${selectedYear}-12-31`)
        );
        
        const snap = await getDocs(q);
        const donorTotals = {};
        let totalRevenue = 0;

        // 3. Aggregate with Roll-up Logic
        snap.docs.forEach(doc => {
          const data = doc.data();
          
          // --- FIX: EXCLUDE OPENING BALANCES ---
          // Opening balances are Equity/Assets, not Donations.
          // Including them skews Average Giving and Risk Concentration.
          if (data.type === 'opening_balance') return; 

          const amount = Number(data.amount) || 0;
          
          const rawId = data.donorId || data.donorName || "Unknown";
          const uniqueUnitId = memberToUnitMap[rawId] || rawId;
          
          donorTotals[uniqueUnitId] = (donorTotals[uniqueUnitId] || 0) + amount;
          totalRevenue += amount;
        });

        const donorValues = Object.values(donorTotals).sort((a, b) => b - a);
        const totalGivers = donorValues.length;
        
        if (totalGivers === 0) {
           setStats({
            totalRevenue: 0, totalGivers: 0, averageGiving: 0, medianGiving: 0, top10Concentration: 0, bands: []
           });
           setLoading(false);
           return;
        }

        // 4. Calculate Stats
        const averageGiving = totalRevenue / totalGivers;
        const medianGiving = donorValues[Math.floor(totalGivers / 2)] || 0;
        const top10Total = donorValues.slice(0, 10).reduce((a, b) => a + b, 0);
        const top10Concentration = totalRevenue > 0 ? (top10Total / totalRevenue) * 100 : 0;

        const bands = [
          { label: '$10,000+', min: 10000, count: 0, total: 0 },
          { label: '$5,000 - $9,999', min: 5000, max: 9999.99, count: 0, total: 0 },
          { label: '$1,000 - $4,999', min: 1000, max: 4999.99, count: 0, total: 0 },
          { label: '$1 - $999', min: 0.01, max: 999.99, count: 0, total: 0 },
        ];

        donorValues.forEach(val => {
          const band = bands.find(b => val >= b.min && (b.max === undefined || val <= b.max));
          if (band) {
              band.count++;
              band.total += val;
          }
        });

        setStats({ totalRevenue, totalGivers, averageGiving, medianGiving, top10Concentration, bands });
      } catch (error) {
        console.error("Analysis Error:", error);
      }
      setLoading(false);
    };

    analyzeGiving();
  }, [churchId, selectedYear]); 

  return (
    <Box sx={{ mt: 2 }} id="giving-analysis-print">
      
      {/* HEADER WITH YEAR SELECTOR */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5">Unique Giving Units Analysis</Typography>
            <Typography variant="body2" color="textSecondary">
                Analysis for Fiscal Year: {selectedYear}
            </Typography>
          </Box>
          
          <TextField
            select
            size="small"
            label="Fiscal Year"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            sx={{ width: 150 }}
            InputProps={{ startAdornment: <CalendarToday fontSize="small" sx={{ mr: 1, color: 'action.active' }} /> }}
          >
            {yearOptions.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </TextField>
      </Box>

      {loading && <LinearProgress sx={{ mb: 3 }} />}

      {!loading && stats.totalGivers === 0 ? (
          <Typography align="center" color="textSecondary" sx={{ py: 5 }}>
              No giving data found for {selectedYear}.
          </Typography>
      ) : (
        <>
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>Total Giving Units</Typography>
                            <Typography variant="h3">{stats.totalGivers}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <People fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="caption">Unique Households & Guests</Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>Average Annual Giving</Typography>
                            <Typography variant="h3">${stats.averageGiving.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <TrendingUp fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
                                <Typography variant="caption">Median: ${stats.medianGiving.toLocaleString()}</Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ bgcolor: stats.top10Concentration > 40 ? '#fff4e5' : 'inherit' }}>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>Top 10 Concentration Risk</Typography>
                            <Typography variant="h3" color={stats.top10Concentration > 40 ? "warning.main" : "inherit"}>
                                {stats.top10Concentration.toFixed(1)}%
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <DonutLarge fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                <Typography variant="caption">
                                    {stats.top10Concentration > 40 ? "High Risk (>40%)" : "Healthy Distribution"}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom>Giving Bands (Household Tiers)</Typography>
            <Table size="small">
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                        <TableCell>Range</TableCell>
                        <TableCell align="right"># of Units</TableCell>
                        <TableCell align="right">% of Givers</TableCell>
                        <TableCell align="right">Total Amount</TableCell>
                        <TableCell align="right">% of Revenue</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {stats.bands.map((band, i) => (
                        <TableRow key={i}>
                            <TableCell>{band.label}</TableCell>
                            <TableCell align="right">{band.count}</TableCell>
                            <TableCell align="right">{stats.totalGivers > 0 ? ((band.count / stats.totalGivers) * 100).toFixed(1) : 0}%</TableCell>
                            <TableCell align="right">${band.total.toLocaleString(undefined, { minimumFractionDigits: 0 })}</TableCell>
                            <TableCell align="right">{stats.totalRevenue > 0 ? ((band.total / stats.totalRevenue) * 100).toFixed(1) : 0}%</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </>
      )}
    </Box>
  );
};

export default GivingAnalysisReport;